import React, {useCallback, useMemo, useState} from 'react';
import {useEventCallback} from 'usehooks-ts';
import {convertEditorTemplatesToDictionary, getActiveSubject} from '../utils';
import {NewEmailTemplate} from '../parts/NewEmailTemplate/NewEmailTemplate';
import {showFormClientSideMessage} from '../../../../../../../Form/actions/messages';
import {priority} from '@priority-software/priority-proto-api/lib';
import {texts} from '../../../../../../../EmailEditor/texts';
import {EmailDialog} from '../../../../../../../EmailEditor/EmailDialog';
import {TEditorTemplatesDictionary} from '../../../../../../../EmailEditor/reducer/types';
import {TAlertMessageDictionary} from '../../../../../../types';
import {TreeNodeProps} from '@priority-software/priority-style-react';
import {replaceIdsWithTitles} from '../../../../../../../EmailEditor/utils/treeActions';

type Props = {
    Messages: TAlertMessageDictionary;
    CoverLetters: TAlertMessageDictionary;
    isDisabledOldTemplate: boolean;
    changeCoverLetter: (coverLetter: TAlertMessageDictionary) => void;
    toggleHasTemporaryEmailTemplate: (flag: boolean) => void;
    dialogTitle?: string;
    dialogDescription?: string;
    emailTemplateFields: TreeNodeProps;
    getEmailTemplateFields: () => Promise<void>;
};

export const useNewTemplate = ({
    CoverLetters,
    isDisabledOldTemplate,
    changeCoverLetter,
    dialogDescription,
    toggleHasTemporaryEmailTemplate,
    dialogTitle,
    emailTemplateFields,
    getEmailTemplateFields
}: Props) => {
    const [isOpenEmailDialog, setIsOpenEmailDialog] = useState(false);
    const [initialTemplatesData, setInitialTemplatesData] = useState<TAlertMessageDictionary>(
        {} satisfies TAlertMessageDictionary
    );

    const templatePreview = getActiveSubject(CoverLetters);
    const formattedSubject = useMemo(() => {
        return replaceIdsWithTitles(templatePreview, emailTemplateFields);
    }, [templatePreview]);

    const handleEmailDialog = useEventCallback(async () => {
        const toggleDialog = async () => {
            if (!isOpenEmailDialog) {
                await getEmailTemplateFields();
                setInitialTemplatesData(CoverLetters);
                setIsOpenEmailDialog(true);
            } else {
                setIsOpenEmailDialog(false);
            }
        };

        if (isDisabledOldTemplate && !isOpenEmailDialog) {
            return new Promise(resolve => {
                showFormClientSideMessage('', priority.netitems.shared.Message.Type.Warning, {
                    title: texts.edit_new_email_message_title,
                    content: texts.edit_new_email_message_content,
                    confirmButtonText: texts.edit_new_email_message_button,
                    cancleButtonText: texts.edit_new_email_message_cancel,
                    onConfirm: () => resolve(toggleDialog()),
                    onClose: () => resolve(null)
                });
            });
        }

        await toggleDialog();
        return null;
    });

    const onEmailSave = useCallback(
        async (templates: TEditorTemplatesDictionary) => {
            toggleHasTemporaryEmailTemplate(true); // Set the flag to true when we have new email template in the rule state

            const dictionary = convertEditorTemplatesToDictionary(templates);
            changeCoverLetter(dictionary);
        },
        [changeCoverLetter]
    );

    const renderNewTemplate = renderComponent => (
        <>
            {isOpenEmailDialog && (
                <EmailDialog
                    isOpen
                    onClose={handleEmailDialog}
                    initialTemplatesData={initialTemplatesData}
                    treeValues={emailTemplateFields}
                    onSave={onEmailSave}
                    subTitle={dialogTitle}
                    description={dialogDescription}
                />
            )}
            <NewEmailTemplate {...{handleEmailDialog, subject: formattedSubject}} />
            {renderComponent()}
        </>
    );

    return {renderNewTemplate} as const;
};
